import React, { useCallback, useReducer, useContext, useEffect } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IUpdateRestaurantPropertiesAsPatchForCategory } from 'types';
import { SaveAsPatchActionTypes } from './saveAsPatchModalActions';
import { saveAsPatchReducer } from './saveAsPatchModalReducer';
import { saveAsPatchInitialState, SaveAsPatchContext, ISaveAsPatchState } from './saveAsPatchModalContext';
import {
	IEditCategoryRestaurantPropertiesContext,
	EditCategoryRestaurantPropertiesContext,
} from '../../../../editCategoryRestaurantPropertiesState/editCategoryRestaurantPropertiesContext';
import { convertUndefinedFields } from 'utils/helpers';
import { updateCategoryRestaurantPropertiesAsPatch } from 'backend/categories';

export const SaveAsPatchState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(saveAsPatchReducer, saveAsPatchInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		refetchRestaurantPropertiesData,
		changeTypeOfModalWindow: changeTypeOfParentModalWindow,
		categoryId,
		customerName,
		fetchedRestaurants,
		IDSelectedRestaurant,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
	} = useContext<IEditCategoryRestaurantPropertiesContext>(EditCategoryRestaurantPropertiesContext);

	useEffect(() => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_INITIAL_FORM_DATA,
			payload: {
				initialFormData: {
					categoryId,
					customerName,
					fetchedRestaurants,
					IDSelectedRestaurantOnRestPropsPage: IDSelectedRestaurant,
					hasSchedulePriority,
					activationSchedule,
					deactivationSchedule,
				},
			},
		});
	}, []);

	const saveData = useCallback(
		async (value: 'saveAndMakeOtherChanges' | 'saveAndClose') => {
			try {
				const dataForSubmit: IUpdateRestaurantPropertiesAsPatchForCategory = {
					categoryId,
					restaurantIds:
						state.typeOfChanges === 'basedChanges'
							? [
									state.initialFormData.IDSelectedRestaurantOnRestPropsPage || '',
									...state.IDsSelectedRestaurant,
							  ]
							: state.IDsSelectedRestaurant,
					update: {
						activationCron: {
							shouldUpdate: state.selectedProperties.includes('activation'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.activationSchedule
									: state.activationSchedule,
						},
						deactivationCron: {
							shouldUpdate: state.selectedProperties.includes('deactivation'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.deactivationSchedule
									: state.deactivationSchedule,
						},
					},
				};
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateCategoryRestaurantPropertiesAsPatch(convertedData);
				if (value === 'saveAndClose') {
					changeTypeOfParentModalWindow(null);
					refetchRestaurantPropertiesData(true);
				}
				if (value === 'saveAndMakeOtherChanges') {
					dispatch({ type: SaveAsPatchActionTypes.RESET_FIELDS });
				}
			} catch (err) {
				dispatch({
					type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'error',
					},
				});
			} finally {
				hideLoader();
			}
		},
		[showLoader, hideLoader, state],
	);

	const setTypeOfChanges = useCallback(
		(value: ISaveAsPatchState['typeOfChanges']) => {
			dispatch({
				type: SaveAsPatchActionTypes.SET_TYPE_OF_CHANGES,
				payload: {
					typeOfChanges: value,
					IDsSelectedRestaurant:
						value === 'basedChanges'
							? state.IDsSelectedRestaurant.filter(
									(id) => id !== state.initialFormData.IDSelectedRestaurantOnRestPropsPage,
							  )
							: state.IDsSelectedRestaurant,
				},
			});
		},
		[state.IDsSelectedRestaurant, state.initialFormData.IDSelectedRestaurantOnRestPropsPage],
	);

	const setIDsRestaurant = useCallback((value: ISaveAsPatchState['IDsSelectedRestaurant']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setProperties = useCallback((value: ISaveAsPatchState['selectedProperties']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_PROPERTIES,
			payload: {
				selectedProperties: value,
			},
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: ISaveAsPatchState['typeOfModalWindow']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<SaveAsPatchContext.Provider
			value={{
				saveData,
				setTypeOfChanges,
				setIDsRestaurant,
				setProperties,
				changeActivationSchedule,
				changeDeactivationSchedule,
				changeTypeOfModalWindow,
				initialFormData: state.initialFormData,
				typeOfChanges: state.typeOfChanges,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				selectedProperties: state.selectedProperties,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</SaveAsPatchContext.Provider>
	);
};
